import { useCallback } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';

import {
    MicrostoreElementState,
    SiteState,
    useGetMicrostoreColumnsPaginatedLazyQuery,
    useGetSimpleMicrostoreSitesLazyQuery,
} from 'data/__generated__';

import { TotemAsyncSelect } from 'components/TotemAsyncSelect';
import { Button } from '@/components/ui/button';
import { Option } from 'components/TotemSelect';

import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';

export type SelectableColumn = { _id: string; name: string; siteName?: string | null };

// react-select types onChange as a union (single | multi | null) because TotemAsyncSelect keeps
// `isMulti` as a loose boolean generic. We only ever use single-select here, so narrow with a
// predicate instead of casting.
const isSingleOption = (value: unknown): value is Option<string> =>
    !!value && typeof value === 'object' && !Array.isArray(value) && 'value' in value && 'label' in value;

// Shared "Colonnes (N)" section used by both the quantity- and the stock-transfer-generation popups:
// scrollable column list + per-row removal + backend search select (optionally scoped to a site) +
// optional "open sites" shortcut. `columns` is owned by the parent popup (which needs the ids for
// its mutations); this component is fully controlled via `onColumnsChange`.
export function ColumnSelectionSection({
    columns,
    onColumnsChange,
    siteId,
    isLocked = false,
    showOpenSitesShortcut = false,
    nextMSDeliveryDate,
}: {
    columns: SelectableColumn[];
    onColumnsChange: (columns: SelectableColumn[]) => void;
    siteId?: string;
    isLocked?: boolean;
    showOpenSitesShortcut?: boolean;
    nextMSDeliveryDate?: string | null;
}) {
    const [searchMicrostoreColumns] = useGetMicrostoreColumnsPaginatedLazyQuery();
    const [getSimpleMicrostoreSites, { loading: openSitesLoading }] = useGetSimpleMicrostoreSitesLazyQuery();

    const addColumn = useCallback(
        (column: SelectableColumn) => {
            if (columns.some(({ _id }) => _id === column._id)) {
                return;
            }
            onColumnsChange([...columns, column]);
        },
        [columns, onColumnsChange],
    );

    const removeColumn = useCallback(
        (columnId: string) => {
            onColumnsChange(columns.filter(({ _id }) => _id !== columnId));
        },
        [columns, onColumnsChange],
    );

    const loadColumnOptions = useCallback(
        async (input: string): Promise<Option<string>[]> => {
            const { data } = await searchMicrostoreColumns({
                variables: {
                    offset: 0,
                    limit: 20,
                    filters: {
                        searchValue: input,
                        states: [MicrostoreElementState.Regular],
                        ...(siteId ? { siteIds: [siteId] } : {}),
                    },
                },
                fetchPolicy: 'cache-and-network',
            });

            return (data?.microstoreColumnsFilter.microstoreColumns ?? []).map(({ _id, name, siteName }) => ({
                label: siteName ? `${name} — ${siteName}` : name,
                value: _id,
            }));
        },
        [searchMicrostoreColumns, siteId],
    );

    const handleAddOpenSitesColumns = useCallback(async () => {
        if (!nextMSDeliveryDate) {
            return;
        }
        const { data } = await getSimpleMicrostoreSites({ fetchPolicy: 'cache-and-network' });
        if (!data) {
            toast.error('Erreur lors de la récupération des sites');
            return;
        }

        const nextMSDeliveryDayIndex = (dateFromString(nextMSDeliveryDate, true).getDay() + 6) % 7;
        const openSiteColumns = data.microstoreSites
            .filter(
                ({ state, deliveryDays, launchDate }) =>
                    state === SiteState.Regular &&
                    deliveryDays[nextMSDeliveryDayIndex] &&
                    launchDate &&
                    nextMSDeliveryDate >=
                        formatDateAsAnniversary({ dateTime: dateFromString(launchDate), useNewFormat: true }),
            )
            .flatMap(({ name, microstoreColumnGroups }) =>
                microstoreColumnGroups.flatMap(({ columns: groupColumns }) =>
                    groupColumns.map(({ _id, name: columnName }) => ({ _id, name: columnName, siteName: name })),
                ),
            );

        const existingIds = new Set(columns.map(({ _id }) => _id));
        const columnsToAdd = openSiteColumns.filter(({ _id }) => !existingIds.has(_id));
        onColumnsChange([...columns, ...columnsToAdd]);
    }, [getSimpleMicrostoreSites, nextMSDeliveryDate, columns, onColumnsChange]);

    return (
        <Section>
            <SectionTitle>Colonnes ({columns.length})</SectionTitle>
            {!isLocked ? (
                <TotemAsyncSelect<string>
                    name="microstoreColumnSearch"
                    label="Ajouter une colonne"
                    placeholder="Rechercher une colonne (nom, site, organisation...)"
                    loadOptions={loadColumnOptions}
                    value={null}
                    isMulti={false}
                    isClearable={false}
                    onChange={(newValue) => {
                        if (isSingleOption(newValue)) {
                            const [name] = newValue.label.split(' — ');
                            addColumn({ _id: newValue.value, name });
                        }
                    }}
                />
            ) : null}
            {showOpenSitesShortcut && !isLocked ? (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={openSitesLoading}
                    onClick={handleAddOpenSitesColumns}
                >
                    Ajouter les colonnes des sites ouverts (prochain jour de livraison)
                </Button>
            ) : null}
            <ColumnList>
                {columns.length ? (
                    columns.map((column) => (
                        <ColumnRow key={column._id} $isLocked={isLocked}>
                            <ColumnInfo>
                                <ColumnName>{column.name}</ColumnName>
                                {column.siteName ? <ColumnSite>{column.siteName}</ColumnSite> : null}
                            </ColumnInfo>
                            {!isLocked ? (
                                <RemoveButton
                                    type="button"
                                    title="Retirer la colonne"
                                    onClick={() => removeColumn(column._id)}
                                >
                                    <X size={16} />
                                </RemoveButton>
                            ) : null}
                        </ColumnRow>
                    ))
                ) : (
                    <EmptyMessage>Aucune colonne sélectionnée</EmptyMessage>
                )}
            </ColumnList>
        </Section>
    );
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 5px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SectionTitle = styled.div`
    text-align: center;
    font-size: 20px;
    font-weight: 800;
`;

const ColumnList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    max-height: 260px;
    overflow-y: auto;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    padding: 5px;
`;

const ColumnRow = styled.div<{ $isLocked: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    opacity: ${({ $isLocked }) => ($isLocked ? 0.6 : 1)};
`;

const ColumnInfo = styled.div`
    display: flex;
    flex-direction: column;
`;

const ColumnName = styled.span`
    font-weight: 600;
`;

const ColumnSite = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const RemoveButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};
    padding: 2px;
`;

const EmptyMessage = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
    padding: 10px;
`;
